import React, { useEffect, useRef, useState } from "react";

import { DeploymentTransferCredentials } from "../../api/deployments";
import { getSshCredentialStatus } from "../../api/sshConnection";
import { Site } from "../../api/sites";

type Props = {
    show: boolean;
    site: Site;
    onClose: () => void;
    onSubmit: (request: DeploymentTransferCredentials) => void | Promise<void>;
};

type TransferForm = {
    sshHost: string;
    sshPort: string;
    sshUsername: string;
    sshAuthType: "PASSWORD" | "PRIVATE_KEY";
    sshPrivateKeyPath: string;
    remoteUploadPath: string;
    strictHostKeyChecking: boolean;
    password: string;
    privateKeyPassphrase: string;
    saveConnection: boolean;
};

const normalizeRemotePath = (path?: string): string =>
    path?.trim().replace(/\\/g, "/").replace(/\/+/g, "/").replace(/\/$/, "") ?? "";

const initialRemoteUploadPath = (site: Site): string => {
    const configured = site.remoteUploadPath?.trim();
    if (!configured) return "deploykit";
    if (normalizeRemotePath(configured) === normalizeRemotePath(site.homePath)) return "deploykit";
    return configured;
};

const createForm = (site: Site): TransferForm => ({
    sshHost: site.sshHost?.trim() ?? "",
    sshPort: String(site.sshPort ?? 22),
    sshUsername: site.sshUsername?.trim() ?? "",
    sshAuthType: site.sshAuthType ?? "PASSWORD",
    sshPrivateKeyPath: site.sshPrivateKeyPath?.trim() ?? "",
    remoteUploadPath: initialRemoteUploadPath(site),
    strictHostKeyChecking: site.strictHostKeyChecking ?? true,
    password: "",
    privateKeyPassphrase: "",
    saveConnection: true,
});

const SshTransferModal: React.FC<Props> = ({ show, site, onClose, onSubmit }) => {
    const [form, setForm] = useState<TransferForm>(() => createForm(site));
    const [validationMessage, setValidationMessage] = useState("");
    const [hasStoredPassword, setHasStoredPassword] = useState(site.hasStoredSshPassword === true);
    const hostInputRef = useRef<HTMLInputElement>(null);
    const secretInputRef = useRef<HTMLInputElement>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const hasSavedConnection = Boolean(site.sshHost?.trim() && site.sshUsername?.trim());

    useEffect(() => {
        let active = true;
        const knownStoredPassword = site.hasStoredSshPassword === true;
        setHasStoredPassword(knownStoredPassword);
        if (!show || site.sshProfileId == null || site.sshAuthType === "PRIVATE_KEY") {
            return () => { active = false; };
        }

        void getSshCredentialStatus(site.sshProfileId)
            .then((status) => {
                if (active) setHasStoredPassword(status.hasStoredPassword);
            })
            .catch((error) => console.error("SSH credential status load failed", error));
        return () => { active = false; };
    }, [show, site.hasStoredSshPassword, site.sshAuthType, site.sshProfileId]);

    useEffect(() => {
        if (!show) return;

        setForm(createForm(site));
        setValidationMessage("");
        const focusTimer = window.setTimeout(() => {
            if (hasSavedConnection && hasStoredPassword) {
                submitButtonRef.current?.focus();
            } else if (hasSavedConnection) {
                secretInputRef.current?.focus();
            } else {
                hostInputRef.current?.focus();
            }
        }, 0);

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.clearTimeout(focusTimer);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [hasSavedConnection, hasStoredPassword, onClose, show, site]);

    if (!show) return null;

    const updateForm = <K extends keyof TransferForm>(key: K, value: TransferForm[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setValidationMessage("");
    };

    const submit = async () => {
        const sshHost = form.sshHost.trim();
        const sshUsername = form.sshUsername.trim();
        const sshPort = Number(form.sshPort);
        const sshPrivateKeyPath = form.sshPrivateKeyPath.trim();
        const remoteUploadPath = form.remoteUploadPath.trim();

        if (!sshHost) {
            setValidationMessage("SSH 호스트를 입력해 주세요.");
            hostInputRef.current?.focus();
            return;
        }
        if (!Number.isInteger(sshPort) || sshPort < 1 || sshPort > 65535) {
            setValidationMessage("SSH 포트는 1~65535 사이의 숫자로 입력해 주세요.");
            return;
        }
        if (!sshUsername) {
            setValidationMessage("SSH 계정을 입력해 주세요.");
            return;
        }
        if (!remoteUploadPath) {
            setValidationMessage("원격 업로드 경로를 입력해 주세요.");
            return;
        }
        let storedPasswordAvailable = hasStoredPassword;
        if (
            form.sshAuthType === "PASSWORD" &&
            !form.password &&
            site.sshProfileId != null
        ) {
            try {
                storedPasswordAvailable = (
                    await getSshCredentialStatus(site.sshProfileId)
                ).hasStoredPassword;
                setHasStoredPassword(storedPasswordAvailable);
            } catch (error) {
                console.error("SSH credential status load failed", error);
            }
        }
        if (form.sshAuthType === "PASSWORD" && !form.password && !storedPasswordAvailable) {
            setValidationMessage("SSH 비밀번호를 입력해 주세요.");
            secretInputRef.current?.focus();
            return;
        }
        if (form.sshAuthType === "PRIVATE_KEY" && !sshPrivateKeyPath) {
            setValidationMessage("개인키 경로를 입력해 주세요.");
            return;
        }

        onClose();
        await onSubmit({
            sshHost,
            sshPort,
            sshUsername,
            sshAuthType: form.sshAuthType,
            sshPrivateKeyPath: form.sshAuthType === "PRIVATE_KEY" ? sshPrivateKeyPath : undefined,
            remoteUploadPath,
            strictHostKeyChecking: form.strictHostKeyChecking,
            password:
                form.sshAuthType === "PASSWORD" && form.password
                    ? form.password
                    : undefined,
            privateKeyPassphrase:
                form.sshAuthType === "PRIVATE_KEY" ? form.privateKeyPassphrase : undefined,
            saveConnection: form.saveConnection,
        });
    };

    return (
        <div
            className="modal dp-modal-backdrop dp-ssh-transfer-backdrop"
            tabIndex={-1}
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <div className="modal-dialog modal-dialog-centered dp-modal-dialog dp-ssh-transfer-dialog">
                <div className="modal-content dp-modal-content">
                    <div className="modal-header dp-modal-header dp-ssh-transfer-header">
                        <div>
                            <h5 className="modal-title">서버 전송 설정</h5>
                            <p>접속 정보를 입력하고 선택한 파일을 바로 전송합니다.</p>
                        </div>
                        <button type="button" className="btn-close" aria-label="닫기" onClick={onClose} />
                    </div>

                    <div className="modal-body dp-modal-body dp-ssh-transfer-body">
                        <div className="dp-ssh-site-summary">
                            <div>
                                <strong>{site.text}</strong>
                                <span>패키지 생성 후 원격 서버로 전송</span>
                            </div>
                            <span className={hasSavedConnection ? "is-saved" : undefined}>
                                {hasSavedConnection ? "저장된 연결" : "신규 연결"}
                            </span>
                        </div>

                        <div className="dp-ssh-basic-grid">
                            <label className="dp-ssh-field dp-ssh-host-field">
                                <span>SSH 호스트</span>
                                <input
                                    ref={hostInputRef}
                                    className="form-control"
                                    type="text"
                                    value={form.sshHost}
                                    placeholder="192.168.0.10 또는 host.example.com"
                                    onChange={(event) => updateForm("sshHost", event.target.value)}
                                />
                            </label>
                            <label className="dp-ssh-field dp-ssh-port-field">
                                <span>포트</span>
                                <input
                                    className="form-control"
                                    type="number"
                                    min={1}
                                    max={65535}
                                    value={form.sshPort}
                                    onChange={(event) => updateForm("sshPort", event.target.value)}
                                />
                            </label>
                            <label className="dp-ssh-field dp-ssh-user-field">
                                <span>SSH 계정</span>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={form.sshUsername}
                                    placeholder="deployer"
                                    onChange={(event) => updateForm("sshUsername", event.target.value)}
                                />
                            </label>
                            <label className="dp-ssh-field">
                                <span>인증 방식</span>
                                <select
                                    className="form-select"
                                    value={form.sshAuthType}
                                    onChange={(event) =>
                                        updateForm(
                                            "sshAuthType",
                                            event.target.value as TransferForm["sshAuthType"]
                                        )
                                    }
                                >
                                    <option value="PASSWORD">비밀번호</option>
                                    <option value="PRIVATE_KEY">개인키</option>
                                </select>
                            </label>
                        </div>

                        {form.sshAuthType === "PASSWORD" ? (
                            <label className="dp-ssh-field">
                                <span>
                                    SSH 비밀번호
                                    {hasStoredPassword ? " (저장됨)" : ""}
                                </span>
                                <input
                                    ref={secretInputRef}
                                    className="form-control"
                                    type="password"
                                    autoComplete="off"
                                    value={form.password}
                                    placeholder={
                                        hasStoredPassword
                                            ? "저장된 비밀번호 사용 · 변경 시 입력"
                                            : "이번 전송에만 사용"
                                    }
                                    onChange={(event) => updateForm("password", event.target.value)}
                                />
                            </label>
                        ) : (
                            <div className="dp-ssh-key-grid">
                                <label className="dp-ssh-field">
                                    <span>개인키 경로</span>
                                    <input
                                        className="form-control"
                                        type="text"
                                        value={form.sshPrivateKeyPath}
                                        placeholder="C:\Users\user\.ssh\id_ed25519"
                                        onChange={(event) =>
                                            updateForm("sshPrivateKeyPath", event.target.value)
                                        }
                                    />
                                </label>
                                <label className="dp-ssh-field">
                                    <span>개인키 암호</span>
                                    <input
                                        ref={secretInputRef}
                                        className="form-control"
                                        type="password"
                                        autoComplete="off"
                                        value={form.privateKeyPassphrase}
                                        placeholder="암호가 없으면 비워두세요"
                                        onChange={(event) =>
                                            updateForm("privateKeyPassphrase", event.target.value)
                                        }
                                    />
                                </label>
                            </div>
                        )}

                        <details className="dp-ssh-advanced">
                            <summary>고급 설정</summary>
                            <div className="dp-ssh-advanced-grid">
                                <label className="dp-ssh-field">
                                    <span>원격 스테이징 경로</span>
                                    <input
                                        className="form-control"
                                        type="text"
                                        value={form.remoteUploadPath}
                                        placeholder="deploykit (SSH 홈 기준)"
                                        onChange={(event) =>
                                            updateForm("remoteUploadPath", event.target.value)
                                        }
                                    />
                                </label>
                                <label className="dp-path-toggle dp-ssh-host-key-toggle">
                                    <input
                                        type="checkbox"
                                        checked={form.strictHostKeyChecking}
                                        onChange={(event) =>
                                            updateForm("strictHostKeyChecking", event.target.checked)
                                        }
                                    />
                                    <span>known_hosts 호스트 키 검증</span>
                                </label>
                            </div>
                        </details>

                        <label className="dp-ssh-save-option">
                            <input
                                type="checkbox"
                                checked={form.saveConnection}
                                onChange={(event) => updateForm("saveConnection", event.target.checked)}
                            />
                            <span>
                                <strong>
                                    {site.sshProfileId
                                        ? "연결된 SSH 프로필에 변경 정보 저장"
                                        : "새 SSH 프로필로 저장하고 이 경로에 연결"}
                                </strong>
                                <small>
                                    원격 업로드 경로는 현재 배포 경로에 별도로 저장됩니다.
                                </small>
                            </span>
                        </label>

                        {validationMessage && (
                            <div className="dp-ssh-validation" role="alert">
                                {validationMessage}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer dp-modal-footer">
                        <button
                            className="btn btn-outline-secondary dp-modal-button dp-modal-button-secondary"
                            type="button"
                            onClick={onClose}
                        >
                            취소
                        </button>
                        <button
                            ref={submitButtonRef}
                            className="btn btn-primary dp-modal-button dp-modal-button-primary"
                            type="button"
                            onClick={submit}
                        >
                            패키지 생성 후 전송
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SshTransferModal;
